import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import ScoreBreakdownModal from "../components/ScoreBreakdownModal";
import InsightModal from "../components/InsightModal";
import { fmtMoney, fmtPct, fmtScore, fmtVolume, recommendationColor } from "../lib/format";
import { Star, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";

const RECOMMENDATIONS = ["Compra forte", "Compra", "Neutro", "Venda", "Venda forte"];

export default function Listing() {
  const [items, setItems] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    exchange: "ALL",
    sector: "ALL",
    recommendation: "ALL",
    sort: "score_desc",
    min_price: "",
    max_price: "",
    min_score: "",
    max_score: "",
  });
  const [scoreStock, setScoreStock] = useState(null);
  const [insightStock, setInsightStock] = useState(null);

  useEffect(() => {
    apiClient.get("/stocks/sectors").then((r) => setSectors(r.data || []));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.exchange !== "ALL") params.exchange = filters.exchange;
      if (filters.sector !== "ALL") params.sector = filters.sector;
      if (filters.recommendation !== "ALL") params.recommendation = filters.recommendation;
      if (filters.sort) params.sort = filters.sort;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      if (filters.min_score) params.min_score = filters.min_score;
      if (filters.max_score) params.max_score = filters.max_score;
      const r = await apiClient.get("/stocks", { params });
      setItems(r.data || []);
    } catch {
      toast.error("Erro ao carregar a listagem.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (s) =>
        s.ticker.toLowerCase().includes(term) ||
        s.company_name.toLowerCase().includes(term),
    );
  }, [items, search]);

  const handleToggleFav = async (stock) => {
    try {
      const r = await apiClient.post("/favorites/toggle", { stock_id: stock.id });
      setItems((prev) =>
        prev.map((s) => (s.id === stock.id ? { ...s, is_favorite: r.data.is_favorite } : s)),
      );
    } catch {
      toast.error("Falha ao atualizar favorito.");
    }
  };

  const clearFilters = () => {
    setFilters({
      exchange: "ALL",
      sector: "ALL",
      recommendation: "ALL",
      sort: "score_desc",
      min_price: "",
      max_price: "",
      min_score: "",
      max_score: "",
    });
    setSearch("");
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1600px] mx-auto" data-testid="listing-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
          Espaço 2 · Universo completo
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
          Listagem com filtros
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Todas as ações capturadas das três bolsas. Filtre por bolsa, setor, recomendação,
          faixa de preço e score.
        </p>
      </div>

      <div className="bg-card border rounded-md p-4 mb-5" data-testid="listing-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ticker ou empresa…"
              className="pl-9 filter-yellow"
              data-testid="listing-search-input"
            />
          </div>
          <Select
            value={filters.exchange}
            onValueChange={(v) => setFilters({ ...filters, exchange: v })}
          >
            <SelectTrigger data-testid="filter-exchange" className="filter-yellow">
              <SelectValue placeholder="Bolsa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas as bolsas</SelectItem>
              <SelectItem value="B3">B3</SelectItem>
              <SelectItem value="NYSE">NYSE</SelectItem>
              <SelectItem value="NASDAQ">NASDAQ</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.sector}
            onValueChange={(v) => setFilters({ ...filters, sector: v })}
          >
            <SelectTrigger data-testid="filter-sector" className="filter-yellow">
              <SelectValue placeholder="Setor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os setores</SelectItem>
              {sectors.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.recommendation}
            onValueChange={(v) => setFilters({ ...filters, recommendation: v })}
          >
            <SelectTrigger data-testid="filter-recommendation" className="filter-yellow">
              <SelectValue placeholder="Recomendação" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              {RECOMMENDATIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.sort}
            onValueChange={(v) => setFilters({ ...filters, sort: v })}
          >
            <SelectTrigger data-testid="filter-sort" className="filter-yellow">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="score_desc">Score ↓</SelectItem>
              <SelectItem value="score_asc">Score ↑</SelectItem>
              <SelectItem value="price_desc">Preço ↓</SelectItem>
              <SelectItem value="price_asc">Preço ↑</SelectItem>
              <SelectItem value="volume_desc">Volume ↓</SelectItem>
              <SelectItem value="change_desc">Variação ↓</SelectItem>
              <SelectItem value="ticker">Ticker A-Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-3">
          <Input
            type="number"
            value={filters.min_price}
            onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
            placeholder="Preço mín."
            className="filter-yellow"
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            value={filters.max_price}
            onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
            placeholder="Preço máx."
            className="filter-yellow"
            data-testid="filter-max-price"
          />
          <Input
            type="number"
            value={filters.min_score}
            onChange={(e) => setFilters({ ...filters, min_score: e.target.value })}
            placeholder="Score mín. (-100)"
            className="filter-yellow"
            data-testid="filter-min-score"
          />
          <Input
            type="number"
            value={filters.max_score}
            onChange={(e) => setFilters({ ...filters, max_score: e.target.value })}
            placeholder="Score máx. (+100)"
            className="filter-yellow"
            data-testid="filter-max-score"
          />
          <Button variant="ghost" onClick={clearFilters} data-testid="filter-clear-button">
            <X className="w-4 h-4 mr-2" /> Limpar
          </Button>
        </div>
      </div>

      <div className="mb-3 text-sm text-muted-foreground" data-testid="listing-count">
        {loading ? "Carregando…" : `${filtered.length} ações`}
      </div>

      <div className="bg-card border rounded-md overflow-x-auto" data-testid="listing-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Ticker</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Bolsa</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead className="text-right">Preço</TableHead>
              <TableHead className="text-right">Var %</TableHead>
              <TableHead className="text-right">Volume</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead>Recomendação</TableHead>
              <TableHead className="text-right">Nota</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-10 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Carregando…
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-10 text-muted-foreground">
                  Nenhuma ação encontrada com os filtros atuais.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((s) => (
                <TableRow key={s.id} data-testid={`listing-row-${s.ticker.toLowerCase()}`}>
                  <TableCell>
                    <button
                      onClick={() => handleToggleFav(s)}
                      className={s.is_favorite ? "text-amber-500" : "text-muted-foreground"}
                      data-testid={`listing-fav-${s.ticker.toLowerCase()}`}
                      aria-label="Favoritar"
                    >
                      <Star className={`w-4 h-4 ${s.is_favorite ? "fill-current" : ""}`} />
                    </button>
                  </TableCell>
                  <TableCell className="font-display font-semibold">
                    <Link
                      to={`/acao/${s.id}`}
                      className="hover:underline underline-offset-4"
                      data-testid={`listing-ticker-${s.ticker.toLowerCase()}`}
                    >
                      {s.ticker}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[260px] truncate">{s.company_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.15em]">
                      {s.exchange}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.sector}</TableCell>
                  <TableCell className="text-right font-mono-tab">
                    {fmtMoney(s.close, s.currency)}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono-tab ${s.change_pct >= 0 ? "text-buy" : "text-sell"}`}
                  >
                    {fmtPct(s.change_pct)}
                  </TableCell>
                  <TableCell className="text-right font-mono-tab">{fmtVolume(s.volume)}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => setScoreStock(s)}
                      className="font-mono-tab font-semibold hover:underline underline-offset-4"
                      data-testid={`listing-score-${s.ticker.toLowerCase()}`}
                    >
                      {fmtScore(s.composite_score)}
                    </button>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-[11px] font-medium px-2 py-0.5 rounded-md border inline-block ${recommendationColor(
                        s.recommendation_label,
                      )}`}
                    >
                      {s.recommendation_label}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setInsightStock(s)}
                      data-testid={`listing-insight-${s.ticker.toLowerCase()}`}
                    >
                      Abrir
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ScoreBreakdownModal
        open={!!scoreStock}
        stock={scoreStock}
        onClose={() => setScoreStock(null)}
      />
      <InsightModal
        open={!!insightStock}
        stock={insightStock}
        onClose={() => setInsightStock(null)}
      />
    </div>
  );
}
